// The viewport's controls (the zoom readout, the scale bar, the minimap's
// rectangle) follow the view at most once a frame, and only when something
// they show has changed.
//
// The engine reports every change of the view as it happens, and has to:
// compare's locked panes follow one another through it, and the review scan
// reads it. A drag moves the view on every pointer event, though, so the
// controls were set — a React render of them — on every pointer event: a
// 1000 Hz mouse made a thousand of them a second, for controls that can
// change sixty times. And a zoom reports its centre and its resolution apart,
// each a new state, though the rounded zoom and magnification shown seldom
// change between them.

import type { ViewState } from './ViewportEngine';

/** Whether two view states show the same thing. */
export function sameViewState(a: ViewState | null, b: ViewState | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const r = a.viewRect;
  const s = b.viewRect;
  return (
    a.magnification === b.magnification &&
    a.magnificationApproximate === b.magnificationApproximate &&
    a.mmPerPixel === b.mmPerPixel &&
    a.zoomPercent === b.zoomPercent &&
    a.rotationDeg === b.rotationDeg &&
    a.canZoomIn === b.canZoomIn &&
    a.canZoomOut === b.canZoomOut &&
    (r === s || (!!r && !!s && r.x === s.x && r.y === s.y && r.w === s.w && r.h === s.h))
  );
}

/**
 * Hand `set` the latest of the states pushed, once a frame. `set` is a React
 * state setter, so a state equal to the one it holds is kept as it is
 * (sameViewState) and nothing renders.
 */
export function viewStateFrames(
  set: (update: (held: ViewState | null) => ViewState | null) => void,
  frame: (cb: () => void) => number = (cb) => requestAnimationFrame(cb),
  cancel: (id: number) => void = (id) => cancelAnimationFrame(id),
): { push: (s: ViewState) => void; stop: () => void } {
  let latest: ViewState | null = null;
  let queued = 0;
  let stopped = false;
  return {
    push: (s) => {
      if (stopped) return;
      latest = s;
      if (queued) return;
      queued = frame(() => {
        queued = 0;
        const next = latest;
        set((held) => (sameViewState(held, next) ? held : next));
      });
    },
    stop: () => {
      stopped = true;
      if (queued) cancel(queued);
      queued = 0;
    },
  };
}
